import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppTopBar } from '../components/AppTopBar';
import { AppSidebar } from '../components/AppSidebar';
import { ArrowLeft, Archive, Check, ChevronLeft, ChevronRight, Inbox, Info, MailPlus, MoreVertical, Trash2, X as XIcon } from 'lucide-react';
import { PageLoadingState } from '../components/LoadingSpinner';
import { DatePicker } from '../components/DatePicker';
import { CreateLeadModal } from '../features/leads/components/CreateLeadModal';
import type { Lead } from '../types';
import { contactDetailPath } from '../utils/contactRoutes';
import {
  inboxPath,
  inboxThreadKey,
  parseInboxEmailIdParam,
  resolveInboxMailForView,
} from '../utils/inboxRoutes';
import { formatDateToDDMMYYYY } from '../utils/date';
import { matchesLeadSelectQuery } from '../utils/leadSelectOption';
import { splitQuotedHtml, splitQuotedPlainText } from '../utils/stripEmailQuotes';
import { buildInboxReplyQuoteBlocks } from '../utils/inboxReplyQuote';
import { isHtmlEditorEmpty, normalizeMailBody } from '../utils/mailBody';
import { markdownToEmailHtml } from '../utils/markdownToEmailHtml';
import { rewriteEmailInlineImages } from '../utils/rewriteEmailInlineImages';
import { ComposeEmailModal } from '../features/inbox/components/ComposeEmailModal';
import { INBOX_ROW_LEAVE_MS, InboxThreadList } from '../features/inbox/components/InboxThreadList';
import { folderRailName, isCategoryFolder } from '../features/inbox/folderNames';
import { AppToastViewport } from '../components/AppToastViewport';
import { useToasts } from '../hooks/useToasts';
import { InboxFolderNav, mailboxDirectionForFolder, type InboxFolderRow } from '../features/inbox/components/InboxFolderNav';
import { InboxConversation } from '../features/inbox/components/InboxConversation';
import { InboxComposeArea } from '../features/inbox/components/InboxComposeArea';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useCapabilities } from '../hooks/useCapabilities';
import { InboxContextPanel } from '../features/inbox/components/InboxContextPanel';

const NO_STATUS_FILTER: string[] = [];
import { patchInboxEmailLeadLink } from '../features/inbox/inboxLeadLink';
import { InboxAttachmentPreviewModal } from '../features/inbox/components/InboxAttachmentPreviewModal';
import { CreateActivityModal } from '../components/activities/CreateActivityModal';
import {
  buildInboxAttachmentDownloadUrl,
  type InboxAttachmentPreviewTarget,
} from '../features/inbox/inboxAttachmentUtils';
import { useInboxAutoSync } from '../hooks/useInboxAutoSync';
import { useInboxLiveEmails, type InboxLiveEvent } from '../hooks/useInboxLiveEmails';
import {
  getCachedInboxAccounts,
  setCachedInboxAccounts,
} from '../utils/inboxSessionCache';
import {
  buildReplyAllCc,
  resolveInboxReplyCc,
  getTriageBucket,
  displayNameFromAddr,
  getCounterpartyAddr,
  getInboxReplyRecipient,
  type InboxAccount,
  type InboxEmail,
  type ChatAttachment,
  type ChatMessage,
  type MailStatus,
  type LeadOption,
  type InboxLeadActivity,
  type DirectionTab,
  type SortMode,
  mapApiLeadToInboxLeadOption,
  dedupeThreadMails,
  mergeInboxEmailRow,
  mergeInboxThreadListRow,
  filterEmailsInThread,
  isOutgoingFromAccount,
  estimateNylasOutgoingBodyBytes,
  mergeNylasAttachmentFiles,
  shouldOfferReplyAll,
  validateNylasAttachments,
  getEffectiveMailStatus,
  parseFiledOnDone,
  type FiledOnDone,
} from '../features/inbox/inboxShared';
import { useInboxThreadPage } from '../features/inbox/useInboxThreadPage';

/** Archive, delete and read act on the whole conversation, like Gmail. */
const THREAD_SCOPE_POST: RequestInit = {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ scope: 'thread' }),
};

function responseIds(raw: unknown, fallback: string | number): Set<string> {
  const ids = Array.isArray(raw) ? raw.map((id) => String(id)) : [];
  return new Set([...ids, String(fallback)]);
}

function withoutThreadOf(rows: InboxEmail[], emailId: string | number): InboxEmail[] {
  const row = rows.find((m) => String(m.id) === String(emailId));
  if (!row) return rows;
  const key = inboxThreadKey(row);
  return rows.filter((m) => inboxThreadKey(m) !== key);
}

function liveRowBelongsToOpenThread(
  row: { id?: unknown; thread_id?: unknown },
  openMailId: string | number | null | undefined,
  openMails: Array<{ id: string | number; thread_id?: string | null }>,
  openMail: { id: string | number; thread_id?: string | null } | null,
): boolean {
  if (openMailId == null) return false;
  const rowId = row.id != null ? String(row.id) : '';
  if (rowId && rowId === String(openMailId)) return true;
  if (openMail && rowId && rowId === String(openMail.id)) return true;
  if (rowId && openMails.some((mail) => String(mail.id) === rowId)) return true;

  const rowThreadId = row.thread_id != null ? String(row.thread_id).trim() : '';
  if (rowThreadId) {
    if (openMail?.thread_id && String(openMail.thread_id) === rowThreadId) return true;
    if (openMails.some((mail) => mail.thread_id != null && String(mail.thread_id) === rowThreadId)) {
      return true;
    }
  }

  if (row.id == null) return false;
  const incomingKey = inboxThreadKey({
    id: row.id as string | number,
    thread_id: (row.thread_id as string | null) ?? null,
  });
  if (openMail && inboxThreadKey(openMail) === incomingKey) return true;
  return openMails.some((mail) => inboxThreadKey(mail) === incomingKey);
}

export function InboxPage() {
  type ReplyMode = 'reply' | 'replyAll';
  const location = useLocation();
  const navigate = useNavigate();
  const { canOpen } = useModuleAccess();
  const { can } = useCapabilities();
  // Projektliste für die Verknüpfung nur mit Leserecht auf Projekte (Modul aus = Recht weg).
  const canViewProjects = can('web.projekte.view');
  const { instanceId, emailId: emailIdParam } = useParams<{ instanceId?: string; emailId?: string }>();
  const urlMailId = parseInboxEmailIdParam(emailIdParam);
  const instancePrefix = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/[^/]+/);
    return match ? match[0] : '/i/default';
  }, [location.pathname]);
  const handleOpenInboxActivity = React.useCallback(
    (activity: InboxLeadActivity) => {
      const leadId = activity.lead_id ?? null;
      const target = leadId != null ? `${instancePrefix}/projects/${leadId}` : `${instancePrefix}/calendar/calendar`;
      if (canOpen(target)) navigate(target);
    },
    [canOpen, instancePrefix, navigate],
  );
  const openInboxMail = React.useCallback(
    (id: string | number | null, options?: { replace?: boolean }) => {
      navigate(inboxPath(id), { replace: options?.replace ?? false });
    },
    [navigate],
  );
  const [accounts, setAccounts] = React.useState<InboxAccount[]>(() => getCachedInboxAccounts() ?? []);
  const [selectedAccountId, setSelectedAccountId] = React.useState<string | number | null>(() => {
    const cached = getCachedInboxAccounts();
    return cached?.[0]?.id ?? null;
  });
  const [loadingAccounts, setLoadingAccounts] = React.useState(() => getCachedInboxAccounts() == null);
  const [syncing, setSyncing] = React.useState(false);
  const [backgroundSyncing, setBackgroundSyncing] = React.useState(false);
  const [syncProgress, setSyncProgress] = React.useState<{ current: number; total: number } | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = React.useState<string | number | null>(null);
  const [archivingId, setArchivingId] = React.useState<string | number | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [linkWarning, setLinkWarning] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [sortMode, setSortMode] = React.useState<SortMode>('datum');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [folderExternalId, setFolderExternalId] = React.useState<string | null>(null);
  const [starredOnly, setStarredOnly] = React.useState(false);
  const [urgentOnly, setUrgentOnly] = React.useState(false);
  const [assignedUserId, setAssignedUserId] = React.useState<string | null>(null);
  const [inboxMembers, setInboxMembers] = React.useState<{ id: string; label: string }[]>([]);
  const [mobileFoldersOpen, setMobileFoldersOpen] = React.useState(false);
  const [mailboxFolders, setMailboxFolders] = React.useState<InboxFolderRow[]>([]);
  const [mailboxCounts, setMailboxCounts] = React.useState({ in: 0, out: 0 });
  const [statusCountsRevision, setStatusCountsRevision] = React.useState(0);
  const selectedFolder = mailboxFolders.find((row) => row.external_folder_id === folderExternalId);
  const folderDirection = mailboxDirectionForFolder(selectedFolder);
  const directionTab = React.useMemo((): DirectionTab => {
    if (starredOnly) return 'alle';
    if (folderDirection) return folderDirection;
    if (!folderExternalId) return 'in';
    return 'alle';
  }, [starredOnly, folderDirection, folderExternalId]);
  const listFolderId = starredOnly || urgentOnly || !selectedFolder || folderDirection ? null : folderExternalId;
  const starredFolder = mailboxFolders.find((folder) => {
    const id = String(folder.external_folder_id ?? '').toUpperCase();
    const name = String(folder.name ?? '').toUpperCase();
    const attrs = folder.attributes ?? [];
    return id === 'STARRED' || name === 'STARRED' || attrs.includes('\\Flagged');
  });
  const folderHeaderCount = starredOnly
    ? starredFolder?.total_count
    : listFolderId
      ? selectedFolder?.total_count
      : null;
  const searchingAll = searchTerm.trim().length > 0;
  /** Mirrors shouldHideCategorizedMail on the server: filed mail leaves the plain Posteingang. */
  const hidesCategorizedMail =
    !searchingAll && !listFolderId && !starredOnly && !urgentOnly && !assignedUserId
    && statusFilter.length === 0 && directionTab === 'in';
  const categoryFolderIds = React.useMemo(
    () => new Set(mailboxFolders.filter(isCategoryFolder).map((folder) => folder.external_folder_id)),
    [mailboxFolders],
  );
  const [leavingIds, setLeavingIds] = React.useState<string[]>([]);
  const { toasts, pushToast, dismissToast } = useToasts();
  const [replyDraft, setReplyDraft] = React.useState('');
  const [replyCc, setReplyCc] = React.useState('');
  const [replyMode, setReplyMode] = React.useState<ReplyMode>('reply');
  const [showReplyCc, setShowReplyCc] = React.useState(false);
  const [replySending, setReplySending] = React.useState(false);
  const replyEditorRef = React.useRef<HTMLDivElement | null>(null);
  const [showReplyModal, setShowReplyModal] = React.useState(false);
  const replyModalEditorRef = React.useRef<HTMLDivElement | null>(null);
  const replyFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [replyAttachments, setReplyAttachments] = React.useState<File[]>([]);
  const [attachmentError, setAttachmentError] = React.useState<string | null>(null);
  const [replyQuoteEnabled, setReplyQuoteEnabled] = React.useState(false);
  const [previewAttachment, setPreviewAttachment] = React.useState<InboxAttachmentPreviewTarget | null>(null);
  const [showAttachmentsModal, setShowAttachmentsModal] = React.useState(false);
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const savedSelectionRef = React.useRef<Range | null>(null);
  const [showSyncSinceModal, setShowSyncSinceModal] = React.useState(false);
  const [showAiReplyModal, setShowAiReplyModal] = React.useState(false);
  const [aiReplyPrompt, setAiReplyPrompt] = React.useState('');
  const [aiReplyLoading, setAiReplyLoading] = React.useState(false);
  const [aiReplyError, setAiReplyError] = React.useState<string | null>(null);
  const [syncSinceDate, setSyncSinceDate] = React.useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });
  const [pendingSyncAccountId, setPendingSyncAccountId] = React.useState<string | number | null>(null);
  const [leadsForLink, setLeadsForLink] = React.useState<LeadOption[]>([]);
  const [linkingEmailId, setLinkingEmailId] = React.useState<string | number | null>(null);
  const [leadDropdownOpen, setLeadDropdownOpen] = React.useState(false);
  const [leadSearch, setLeadSearch] = React.useState('');
  const leadDropdownRef = React.useRef<HTMLDivElement>(null);
  const [showCreateLeadModal, setShowCreateLeadModal] = React.useState(false);
  const [showCreateActivityModal, setShowCreateActivityModal] = React.useState(false);
  const [activitiesRefreshKey, setActivitiesRefreshKey] = React.useState(0);
  const [showComposeModal, setShowComposeModal] = React.useState(false);
  const [composeResetKey, setComposeResetKey] = React.useState(0);
  const [composeInitial, setComposeInitial] = React.useState<{
    to?: string;
    cc?: string;
    subject?: string;
    body?: string;
    forwardSourceEmailId?: string | number | null;
    forwardAttachments?: InboxAttachmentPreviewTarget[];
  } | null>(null);
  const [showMobileDetails, setShowMobileDetails] = React.useState(false);
  const threadScrollRef = React.useRef<HTMLDivElement | null>(null);
  const [showMobileMailMenu, setShowMobileMailMenu] = React.useState(false);
  const [threadMails, setThreadMails] = React.useState<InboxEmail[]>([]);
  const [threadLoading, setThreadLoading] = React.useState(false);
  const [deepLinkMail, setDeepLinkMail] = React.useState<InboxEmail | null>(null);
  const urlMailIdRef = React.useRef(urlMailId);
  urlMailIdRef.current = urlMailId;
  const threadMailsRef = React.useRef(threadMails);
  threadMailsRef.current = threadMails;
  const selectedMailRef = React.useRef<InboxEmail | null>(null);
  const mobileMailMenuRef = React.useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  React.useEffect(() => {
    if (!showMobileMailMenu) return;
    const handler = (e: MouseEvent) => {
      if (mobileMailMenuRef.current && !mobileMailMenuRef.current.contains(e.target as Node)) {
        setShowMobileMailMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMobileMailMenu]);

  const sanitizeMailHtml = React.useCallback((html: string): string => {
    if (!html) return '';
    // <style> bewusst behalten — sonst brechen HTML-/Marketing-Mail-Layouts.
    // Gefährliche CSS-Teile werden in prepareMailHtmlForPreview bereinigt.
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/\son\w+="[^"]*"/gi, '')
      .replace(/\son\w+='[^']*'/gi, '')
      .replace(/href="javascript:[^"]*"/gi, 'href="#"')
      .replace(/href='javascript:[^']*'/gi, "href='#'");
  }, []);

  const rewriteInlineMailHtml = React.useCallback(
    (html: string, mail: InboxEmail): string => {
      if (!html) return html;
      if (!mail.account_id || !mail.external_message_id) return html;
      const attachments = Array.isArray(mail.attachments) ? mail.attachments : [];
      if (attachments.length === 0) return html;
      return rewriteEmailInlineImages(html, {
        accountId: mail.account_id,
        messageId: mail.external_message_id,
        attachments,
      });
    },
    []
  );

  const renderTextWithLinks = React.useCallback((text: string) => {
    const result: React.ReactNode[] = [];
    const linkToken = /\[\[LINK\|([^|]+)\|([^\]]+)\]\]/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const pushPlainWithUrls = (chunk: string) => {
      if (!chunk) return;
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const pieces = chunk.split(urlRegex);
      pieces.forEach((part, idx) => {
        if (idx % 2 === 1) {
          result.push(
            <a
              key={`auto-url-${result.length}`}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-pipedrive-blue break-all"
            >
              {part}
            </a>
          );
        } else if (part) {
          result.push(<span key={`auto-text-${result.length}`}>{part}</span>);
        }
      });
    };

    while ((match = linkToken.exec(text)) !== null) {
      const before = text.slice(lastIndex, match.index);
      pushPlainWithUrls(before);
      const label = match[1];
      const href = match[2];
      result.push(
        <a
          key={`link-token-${result.length}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-pipedrive-blue break-all"
        >
          {label}
        </a>
      );
      lastIndex = linkToken.lastIndex;
    }

    const rest = text.slice(lastIndex);
    pushPlainWithUrls(rest);

    return result;
  }, []);

  const openInboxAttachment = React.useCallback((attachment: InboxAttachmentPreviewTarget) => {
    setAttachmentError(null);
    setPreviewAttachment(attachment);
  }, []);

  const loadAccounts = React.useCallback((options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoadingAccounts(true);
    fetch('/api/inbox/accounts')
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setAccounts(list);
        setCachedInboxAccounts(list);
        const ids = list.map((a: { id: string | number }) => String(a.id));
        setSelectedAccountId((prev) =>
          prev != null && ids.includes(String(prev)) ? prev : (list[0]?.id ?? null),
        );
      })
      .catch(() => {
        if (!silent) setAccounts([]);
      })
      .finally(() => {
        if (!silent) setLoadingAccounts(false);
      });
  }, []);

  React.useEffect(() => {
    if (getCachedInboxAccounts()) {
      void loadAccounts({ silent: true });
      return;
    }
    loadAccounts();
  }, [loadAccounts]);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/inbox-reply', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json() as Promise<{ reply_quote_enabled?: boolean }>;
      })
      .then((data) => {
        if (cancelled || !data) return;
        setReplyQuoteEnabled(Boolean(data.reply_quote_enabled));
      })
      .catch(() => {
        // Keep default (false) if settings cannot be loaded.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedAccount = accounts.find((a) => String(a.id) === String(selectedAccountId));
  const accountEmail = selectedAccount?.email || '';

  const parseEmailsWithAttachments = React.useCallback((data: any[]): InboxEmail[] => {
    return (Array.isArray(data) ? data : []).map((m: any) => {
      let attachments: ChatAttachment[] | undefined;
      if (typeof m.attachments_json === 'string') {
        try {
          const parsed = JSON.parse(m.attachments_json as string);
          if (Array.isArray(parsed)) attachments = parsed;
        } catch {
          // ignore
        }
      } else if (Array.isArray(m.attachments_json)) {
        attachments = m.attachments_json as ChatAttachment[];
      }
      return { ...m, attachments } as InboxEmail;
    });
  }, []);

  const applyLiveRowToOpenThread = React.useCallback(
    (_event: InboxLiveEvent, row: Record<string, unknown>) => {
      const openMailId = urlMailIdRef.current;
      if (openMailId == null) return;
      const openMails = threadMailsRef.current;
      const openMail = selectedMailRef.current;
      if (!liveRowBelongsToOpenThread(row, openMailId, openMails, openMail)) return;

      const emailId = row.id != null ? String(row.id) : '';
      if (row.deleted_at != null || row.archived_at != null) {
        if (!emailId) return;
        setThreadMails((prev) => prev.filter((mail) => String(mail.id) !== emailId));
        return;
      }

      const [parsed] = parseEmailsWithAttachments([row as unknown as InboxEmail]);
      if (!parsed?.id) return;
      if (!String(parsed.body_html || '').trim()) delete parsed.body_html;
      if (!String(parsed.body_text || '').trim()) delete parsed.body_text;
      setThreadMails((prev) => mergeInboxEmailRow(prev, parsed));

      const hasBody = Boolean(String(parsed.body_html || parsed.body_text || '').trim());
      const alreadyLoaded = openMails.some(
        (mail) =>
          String(mail.id) === String(parsed.id) &&
          Boolean(String(mail.body_html || mail.body_text || '').trim()),
      );
      if (hasBody || alreadyLoaded) return;

      fetch(`/api/inbox/emails/${encodeURIComponent(String(parsed.id))}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((fullRow) => {
          if (!fullRow || urlMailIdRef.current == null) return;
          if (
            !liveRowBelongsToOpenThread(
              fullRow,
              urlMailIdRef.current,
              threadMailsRef.current,
              selectedMailRef.current,
            )
          ) {
            return;
          }
          const [fullParsed] = parseEmailsWithAttachments([fullRow as InboxEmail]);
          if (!fullParsed?.id) return;
          setThreadMails((prev) => mergeInboxEmailRow(prev, fullParsed));
        })
        .catch(() => {
          /* Conversation stays on the live snippet until the next refresh. */
        });
    },
    [parseEmailsWithAttachments],
  );

  const threadPage = useInboxThreadPage({
    accountId: selectedAccountId,
    accountEmail,
    directionTab: searchingAll ? 'alle' : directionTab,
    statusFilter: searchingAll ? NO_STATUS_FILTER : statusFilter,
    searchTerm,
    sortMode,
    folderExternalId: searchingAll ? null : listFolderId,
    starredOnly: searchingAll ? false : starredOnly,
    urgentOnly: searchingAll ? false : urgentOnly,
    assignedUserId: searchingAll ? null : assignedUserId,
    hiddenFolderIds: hidesCategorizedMail ? categoryFolderIds : null,
  });
  const {
    items: emails,
    setItems: setEmails,
    counts: directionCounts,
    hasMore,
    pageIndex,
    pageSize,
    loading: loadingEmails,
    loadingMore,
    silentRefreshing: emailsSilentRefreshing,
    loadedAccountId: loadedEmailsAccountId,
    error: listError,
    goNext,
    goPrev,
    reload: reloadEmails,
    applyLiveRow,
  } = threadPage;

  React.useEffect(() => {
    setMailboxCounts({ in: 0, out: 0 });
  }, [selectedAccountId]);

  React.useEffect(() => {
    if (selectedAccountId == null || String(loadedEmailsAccountId ?? '') !== String(selectedAccountId)) return;
    if (searchingAll || listFolderId || starredOnly || urgentOnly || statusFilter.length > 0 || assignedUserId) return;
    setMailboxCounts({ in: directionCounts.in, out: directionCounts.out });
  }, [
    selectedAccountId,
    loadedEmailsAccountId,
    listFolderId,
    starredOnly,
    urgentOnly,
    statusFilter,
    assignedUserId,
    directionCounts,
    searchingAll,
  ]);

  React.useEffect(() => {
    if (listError) setError(listError);
  }, [listError]);

  const runSync = React.useCallback(
    async (accountId: string | number, sinceOrOptions?: string | { since?: string; silent?: boolean }) => {
      const since = typeof sinceOrOptions === 'string' ? sinceOrOptions : sinceOrOptions?.since;
      const silent = typeof sinceOrOptions === 'object' && sinceOrOptions?.silent === true;
      if (silent) setBackgroundSyncing(true);
      else {
        setSyncing(true);
        setSyncProgress(null);
      }
      if (!silent) setError(null);
      const body = since ? JSON.stringify({ since }) : undefined;
      try {
        const res = await fetch(`/api/inbox/accounts/${accountId}/sync`, {
          method: 'POST',
          headers: body ? { 'Content-Type': 'application/json' } : undefined,
          body,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const msg = data?.error || 'Sync fehlgeschlagen';
          if (!silent && res.status === 400 && String(msg).toLowerCase().includes('startdatum')) {
            setPendingSyncAccountId(accountId);
            setShowSyncSinceModal(true);
            return;
          }
          if (!silent) throw new Error(msg);
          return;
        }
        const data = await res.json();
        if (data.error) {
          if (!silent) throw new Error(data.error);
          return;
        }
        if (selectedAccountId != null && String(selectedAccountId) === String(accountId)) {
          await reloadEmails({ silent: true });
          loadAccounts({ silent: true });
          setStatusCountsRevision((revision) => revision + 1);
        }
      } catch (err: unknown) {
        if (!silent) setError(err instanceof Error ? err.message : 'Sync fehlgeschlagen');
      } finally {
        if (silent) setBackgroundSyncing(false);
        else {
          setSyncing(false);
          setSyncProgress(null);
        }
      }
    },
    [selectedAccountId, reloadEmails, loadAccounts],
  );

  const selectedAccountForSync = React.useMemo(
    () => accounts.find((a) => String(a.id) === String(selectedAccountId)) ?? null,
    [accounts, selectedAccountId],
  );

  useInboxAutoSync({
    account: selectedAccountForSync,
    onSync: runSync,
  });

  useInboxLiveEmails({
    instanceId: instanceId ?? null,
    accountId: selectedAccountId,
    onRowChange: (event, row) => {
      applyLiveRow(event, row);
      applyLiveRowToOpenThread(event, row);
    },
  });

  const handleSync = (accountId: string | number) => {
    const account = accounts.find((a) => String(a.id) === String(accountId));
    if (account?.sync_since == null) {
      setPendingSyncAccountId(accountId);
      setShowSyncSinceModal(true);
      return;
    }
    runSync(accountId);
  };

  const handleSyncSinceConfirm = () => {
    if (pendingSyncAccountId == null) return;
    runSync(pendingSyncAccountId, syncSinceDate);
    setShowSyncSinceModal(false);
    setPendingSyncAccountId(null);
    loadAccounts(); // Aktualisiert sync_since in der Kontenliste
  };

  const handleToggleStar = (mail: InboxEmail) => {
    const next = mail.starred !== true;
    setEmails((prev) => prev.map((row) => (String(row.id) === String(mail.id) ? { ...row, starred: next } : row)));
    void fetch(`/api/inbox/emails/${mail.id}/star`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ starred: next }),
    });
  };

  /** Rows collapse first, then drop out; the reload after that refills the page and counts. */
  const leaveInboxList = (emailIds: string[]): Promise<void> => {
    const ids = emailIds.map(String);
    if (ids.length === 0) return Promise.resolve();
    setLeavingIds((prev) => [...new Set([...prev, ...ids])]);
    return new Promise((resolve) => {
      window.setTimeout(() => {
        setEmails((prev) => prev.filter((row) => !ids.includes(String(row.id))));
        setLeavingIds((prev) => prev.filter((id) => !ids.includes(id)));
        resolve();
      }, INBOX_ROW_LEAVE_MS);
    });
  };

  /** pending=true sets the folder aside for "Erledigt" instead of moving the mail. */
  const postCategory = (emailId: string | number, folderId: string, assigned: boolean, opts?: { pending?: boolean }) =>
    fetch(`/api/inbox/emails/${emailId}/folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder_external_id: folderId, assigned, pending: opts?.pending === true }),
    }).catch(() => null);

  const setRowCategory = (mail: InboxEmail, folderId: string, assigned: boolean) => {
    const current = mail.provider_folder_ids ?? [];
    const next = assigned ? Array.from(new Set([...current, folderId])) : current.filter((id) => id !== folderId);
    const pending = assigned || mail.pending_folder_id === folderId ? null : mail.pending_folder_id ?? null;
    const patch = { provider_folder_ids: next, pending_folder_id: pending };
    setEmails((prev) => prev.map((row) => (String(row.id) === String(mail.id) ? { ...row, ...patch } : row)));
    setThreadMails((prev) => prev.map((row) => (String(row.id) === String(mail.id) ? { ...row, ...patch } : row)));
  };

  const assignmentLeavesList = (folderId: string) => hidesCategorizedMail && categoryFolderIds.has(folderId);

  const categoryLabel = (folderId: string) => {
    const folder = mailboxFolders.find((row) => row.external_folder_id === folderId);
    return folder ? folderRailName(folder.name || folder.display_name).label : 'Ordner';
  };

  /** Gmail-style notice. Undo waits for the move, then takes the category off again. */
  const pushCategoryUndo = (emailIds: string[], folderId: string, leaves: boolean, moved: Promise<unknown>) => {
    if (emailIds.length === 0) return;
    const label = categoryLabel(folderId);
    const subject = emailIds.length === 1 ? 'Unterhaltung' : `${emailIds.length} Unterhaltungen`;
    const message = leaves ? `${subject} nach „${label}“ verschoben` : `${subject} zu „${label}“ hinzugefügt`;
    pushToast(message, 'info', {
      label: 'Rückgängig',
      onClick: () => {
        void moved
          .then(() => Promise.all(emailIds.map((id) => postCategory(id, folderId, false))))
          .then(() => {
            void reloadEmails({ silent: true });
            setStatusCountsRevision((revision) => revision + 1);
          });
      },
    });
  };

  const handleToggleCategory = (mail: InboxEmail, folderId: string, assigned: boolean) => {
    setRowCategory(mail, folderId, assigned);
    const leaves = assigned && assignmentLeavesList(folderId);
    const leaving = leaves ? leaveInboxList([String(mail.id)]) : Promise.resolve();
    const done = Promise.all([postCategory(mail.id, folderId, assigned), leaving]).then(() => {
      if (leaves) void reloadEmails({ silent: true });
      setStatusCountsRevision((revision) => revision + 1);
    });
    if (assigned) pushCategoryUndo([String(mail.id)], folderId, leaves, done);
  };

  const uniqueDragEmailIds = (emailIds: string[]) =>
    [...new Set(emailIds.map((id) => String(id).trim()).filter(Boolean))];

  const handleAssignEmailsToCategory = (emailIds: string[], folderId: string) => {
    const uniqueIds = uniqueDragEmailIds(emailIds);
    const requests: Promise<unknown>[] = [];
    const listedIds: string[] = [];
    const movedIds: string[] = [];
    for (const id of uniqueIds) {
      const mail = emails.find((row) => String(row.id) === id);
      if (mail) {
        if ((mail.provider_folder_ids ?? []).includes(folderId)) continue;
        setRowCategory(mail, folderId, true);
        listedIds.push(id);
      }
      movedIds.push(id);
      requests.push(postCategory(id, folderId, true));
    }
    const leaves = assignmentLeavesList(folderId) && listedIds.length > 0;
    if (leaves) requests.push(leaveInboxList(listedIds));
    const done = Promise.all(requests).then(() => {
      if (leaves) void reloadEmails({ silent: true });
      setStatusCountsRevision((revision) => revision + 1);
    });
    pushCategoryUndo(movedIds, folderId, leaves, done);
  };

  const handleArchive = (emailId: string | number) => {
    setArchivingId(emailId);
    fetch(`/api/inbox/emails/${emailId}/archive`, THREAD_SCOPE_POST)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d?.error || 'Archivieren fehlgeschlagen');
        // The whole conversation leaves the list, like Gmail. Sent mail may bring it back on reload.
        const gone = responseIds(d?.archived_ids, emailId);
        setThreadMails((prev) => prev.filter((m) => !gone.has(String(m.id))));
        setEmails((prev) => withoutThreadOf(prev, emailId));
        void reloadEmails({ silent: true });
        setStatusCountsRevision((revision) => revision + 1);
        if (urlMailId != null && String(urlMailId) === String(emailId)) {
          openInboxMail(null, { replace: true });
        }
      })
      .catch((err: any) => setError(err?.message || 'Archivieren fehlgeschlagen'))
      .finally(() => setArchivingId(null));
  };

  /** openNext: from the open mail, show this one instead of going back to the list. */
  const handleDelete = (emailId: string | number, opts?: { openNext?: string | number | null }) => {
    setDeletingId(emailId);
    fetch(`/api/inbox/emails/${emailId}/delete`, THREAD_SCOPE_POST)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d?.error || 'Löschen fehlgeschlagen');
        const gone = responseIds(d?.deleted_ids, emailId);
        setThreadMails((prev) => prev.filter((m) => !gone.has(String(m.id))));
        setEmails((prev) => withoutThreadOf(prev, emailId));
        void reloadEmails({ silent: true });
        setStatusCountsRevision((revision) => revision + 1);
        if (urlMailId != null && String(urlMailId) === String(emailId)) {
          openInboxMail(opts?.openNext ?? null, { replace: true });
        }
      })
      .catch((err: any) => setError(err?.message || 'Löschen fehlgeschlagen'))
      .finally(() => setDeletingId(null));
  };

  const handleDeleteRef = React.useRef(handleDelete);
  handleDeleteRef.current = handleDelete;

  const mailById = React.useCallback(
    (emailId: string | number) =>
      threadMails.find((mail) => String(mail.id) === String(emailId))
      ?? emails.find((mail) => String(mail.id) === String(emailId))
      ?? (deepLinkMail && String(deepLinkMail.id) === String(emailId) ? deepLinkMail : null),
    [threadMails, emails, deepLinkMail],
  );

  const patchMail = (
    emailId: string | number,
    threadId: string | null | undefined,
    patch: Partial<InboxEmail>,
    threadWide: boolean,
  ) => {
    const matches = (mail: InboxEmail) =>
      threadWide && threadId
        ? mail.thread_id === threadId
        : String(mail.id) === String(emailId);
    setEmails((prev) => prev.map((mail) => (matches(mail) ? { ...mail, ...patch } : mail)));
    setThreadMails((prev) => prev.map((mail) => (matches(mail) ? { ...mail, ...patch } : mail)));
    setDeepLinkMail((prev) => (prev && matches(prev) ? { ...prev, ...patch } : prev));
  };

  const postClassification = (emailId: string | number, classification: MailStatus, urgency: 'low' | 'normal' | 'high') =>
    fetch(`/api/inbox/emails/${emailId}/classification`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ classification, urgency_level: urgency, is_urgent: urgency === 'high' }),
    }).catch(() => null);

  /**
   * "Erledigt" moved mail into the folder the AI had set aside. The row leaves the Posteingang;
   * undo takes it out again, sets the folder aside once more and restores the old status.
   */
  const handleFiledOnDone = (
    filed: FiledOnDone[],
    restore: Array<{ id: string; classification: MailStatus; urgency: 'low' | 'normal' | 'high' }>,
  ) => {
    if (filed.length === 0) return;
    const folderById = new Map(filed.map((entry) => [entry.id, entry.folder_external_id]));
    const filedPatch = (row: InboxEmail): InboxEmail => {
      const folderId = folderById.get(String(row.id));
      if (!folderId) return row;
      return { ...row, provider_folder_ids: [...new Set([...(row.provider_folder_ids ?? []), folderId])], pending_folder_id: null };
    };
    setEmails((prev) => prev.map(filedPatch));
    setThreadMails((prev) => prev.map(filedPatch));
    const listIds = restore.map((entry) => entry.id).filter((id) => folderById.has(id));
    const leaves = hidesCategorizedMail && listIds.length > 0;
    const moved = (leaves ? leaveInboxList(listIds) : Promise.resolve()).then(() => {
      if (leaves) void reloadEmails({ silent: true });
      setStatusCountsRevision((revision) => revision + 1);
    });
    const folderIds = [...new Set(filed.map((entry) => entry.folder_external_id))];
    const target = folderIds.length === 1 ? `nach „${categoryLabel(folderIds[0])}“` : `in ${folderIds.length} Ordner`;
    const subject = restore.length === 1 ? 'Erledigt' : `${restore.length} Unterhaltungen erledigt`;
    pushToast(`${subject} und ${target} verschoben`, 'info', {
      label: 'Rückgängig',
      onClick: () => {
        void moved
          .then(() => Promise.all(filed.map((entry) => postCategory(entry.id, entry.folder_external_id, false, { pending: true }))))
          .then(() => Promise.all(restore.map((entry) => postClassification(entry.id, entry.classification, entry.urgency))))
          .then(() => {
            void reloadEmails({ silent: true });
            setStatusCountsRevision((revision) => revision + 1);
          });
      },
    });
  };

  const handleChangeStatus = (emailId: string | number, status: MailStatus) => {
    const current = mailById(emailId);
    const urgency = current?.urgency_level === 'low' || current?.urgency_level === 'high' || current?.urgency_level === 'normal'
      ? current.urgency_level
      : 'normal';
    const previous = current ? getEffectiveMailStatus(current) : 'offen';
    setUpdatingStatusId(emailId);
    fetch(`/api/inbox/emails/${emailId}/classification`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ classification: status, urgency_level: urgency, is_urgent: urgency === 'high' }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d?.error || 'Klassifikation konnte nicht aktualisiert werden');
        patchMail(emailId, current?.thread_id, { classification: status, urgency_level: urgency, is_urgent: urgency === 'high' }, false);
        setStatusCountsRevision((revision) => revision + 1);
        handleFiledOnDone(parseFiledOnDone(d?.filed), [{ id: String(emailId), classification: previous, urgency }]);
      })
      .catch((err) => setError(err.message || 'Klassifikation konnte nicht aktualisiert werden'))
      .finally(() => setUpdatingStatusId(null));
  };

  const handleChangeUrgency = (emailId: string | number, level: 'low' | 'normal' | 'high') => {
    const current = mailById(emailId);
    const classification = (current?.classification as MailStatus) || 'other';
    setUpdatingStatusId(emailId);
    fetch(`/api/inbox/emails/${emailId}/classification`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ classification, urgency_level: level, is_urgent: level === 'high' }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d?.error || 'Dringlichkeit konnte nicht aktualisiert werden');
        patchMail(emailId, current?.thread_id, { urgency_level: level, is_urgent: level === 'high' }, false);
        setStatusCountsRevision((revision) => revision + 1);
      })
      .catch((err) => setError(err.message || 'Dringlichkeit konnte nicht aktualisiert werden'))
      .finally(() => setUpdatingStatusId(null));
  };

  const handleAssignUser = (emailId: string | number, userId: string | null, threadId: string | null) => {
    setUpdatingStatusId(emailId);
    fetch(`/api/inbox/emails/${emailId}/assignee`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d?.error || 'Zuweisung konnte nicht gespeichert werden');
        patchMail(emailId, threadId, { assigned_user_id: userId }, true);
        setStatusCountsRevision((revision) => revision + 1);
        void reloadEmails({ silent: true });
      })
      .catch((err) => setError(err.message || 'Zuweisung konnte nicht gespeichert werden'))
      .finally(() => setUpdatingStatusId(null));
  };

  const handleAssignEmailsUrgent = (emailIds: string[]) => {
    for (const id of uniqueDragEmailIds(emailIds)) {
      const mail = mailById(id);
      if (mail?.is_urgent === true || mail?.urgency_level === 'high') continue;
      handleChangeUrgency(id, 'high');
    }
  };

  const handleAssignEmailsToStatus = (emailIds: string[], status: MailStatus) => {
    for (const id of uniqueDragEmailIds(emailIds)) {
      const mail = mailById(id);
      if (mail && getEffectiveMailStatus(mail) === status) continue;
      handleChangeStatus(id, status);
    }
  };

  const handleAssignEmailsToUser = (emailIds: string[], userId: string) => {
    for (const id of uniqueDragEmailIds(emailIds)) {
      const mail = mailById(id);
      if (mail && String(mail.assigned_user_id ?? '') === userId) continue;
      handleAssignUser(id, userId, mail?.thread_id ?? null);
    }
  };

  /** Server lowers the stored folder unread counts; reload the sidebar so they show. */
  const markInboxEmailRead = async (id: string | number) => {
    try {
      const res = await fetch(`/api/inbox/emails/${id}/read`, THREAD_SCOPE_POST);
      if (res.ok) setStatusCountsRevision((revision) => revision + 1);
    } catch {
      // Sidebar counts catch up on the next reload or sync.
    }
  };

  const handleBulkAction = async (mails: InboxEmail[], action: 'archive' | 'delete' | 'done') => {
    const ids = mails.map((mail) => mail.id);
    if (ids.length === 0) return;
    const idSet = new Set(ids.map((id) => String(id)));
    const restore: Array<{ id: string; classification: MailStatus; urgency: 'low' | 'normal' | 'high' }> = [];
    if (action === 'done') {
      setEmails((prev) => prev.map((row) => (idSet.has(String(row.id)) ? { ...row, classification: 'done' } : row)));
    } else {
      setEmails((prev) => prev.filter((row) => !idSet.has(String(row.id))));
      if (urlMailId != null && idSet.has(String(urlMailId))) openInboxMail(null, { replace: true });
    }
    const results = await Promise.all(
      ids.map(async (id) => {
        const mail = mails.find((row) => String(row.id) === String(id));
        const urgency = mail?.urgency_level === 'low' || mail?.urgency_level === 'high' || mail?.urgency_level === 'normal'
          ? mail.urgency_level
          : 'normal';
        const res = await fetch(
          action === 'done' ? `/api/inbox/emails/${id}/classification` : `/api/inbox/emails/${id}/${action}`,
          action === 'done'
            ? { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ classification: 'done', urgency_level: urgency, is_urgent: urgency === 'high' }) }
            : THREAD_SCOPE_POST,
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: String(data?.error || 'Aktion fehlgeschlagen'), filed: [] as FiledOnDone[] };
        const filed = action === 'done' ? parseFiledOnDone(data?.filed) : [];
        // Undo only touches conversations that "Erledigt" actually moved.
        if (filed.length > 0 && mail) {
          restore.push({ id: String(id), classification: getEffectiveMailStatus(mail), urgency });
        }
        return { error: null, filed };
      }),
    );
    const failed = results.find((result) => result.error)?.error;
    if (failed) setError(failed);
    handleFiledOnDone(results.flatMap((result) => result.filed), restore);
    void reloadEmails({ silent: true });
    setStatusCountsRevision((revision) => revision + 1);
  };

  const filteredLeadsForLink = React.useMemo(() => {
    return leadsForLink.filter((l) => matchesLeadSelectQuery(l, leadSearch));
  }, [leadsForLink, leadSearch]);

  React.useEffect(() => {
    fetch('/api/me/members', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { members: [] }))
      .then((data) => {
        const list = Array.isArray(data?.members) ? data.members : [];
        setInboxMembers(
          list
            .filter((member: { platform_access?: string; capabilities?: Record<string, boolean> }) => {
              if (member.platform_access === 'field_only') return false;
              const caps = member.capabilities ?? {};
              return Boolean(caps['web.posteingang.view'] || caps['web.posteingang.edit']);
            })
            .map((member: { user_id?: string; email?: string; display_name?: string | null }) => ({
              id: String(member.user_id ?? ''),
              label: String(member.display_name ?? '').trim() || String(member.email ?? '').trim() || 'Nutzer',
            }))
            .filter((member: { id: string }) => member.id.length > 0)
            .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, 'de')),
        );
      })
      .catch(() => setInboxMembers([]));
  }, []);

  React.useEffect(() => {
    if (!urlMailId || !canViewProjects) return;
    fetch('/api/leads')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const arr = Array.isArray(list)
          ? list.map((l: Lead & { contact?: LeadOption['contact']; baustelle?: Lead['baustelle'] }) =>
              mapApiLeadToInboxLeadOption(l),
            )
          : [];
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
        setLeadsForLink(arr);
      })
      .catch(() => setLeadsForLink([]));
  }, [urlMailId, canViewProjects]);

  React.useEffect(() => {
    if (!leadDropdownOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (leadDropdownRef.current && !leadDropdownRef.current.contains(e.target as Node)) setLeadDropdownOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [leadDropdownOpen]);

  const handleLinkToLead = (
    emailId: string | number,
    leadId: string | number | null,
    threadId: string | null,
  ): Promise<void> => {
    setLinkingEmailId(emailId);
    return patchInboxEmailLeadLink(emailId, leadId)
      .then((result) => {
        if (result.ok === false) throw new Error(result.error);
        const newLeadId = leadId ?? undefined;
        setEmails((prev) =>
          prev.map((m) => {
            if (threadId && m.thread_id === threadId) return { ...m, lead_id: newLeadId };
            if (String(m.id) === String(emailId)) return { ...m, lead_id: newLeadId };
            return m;
          }),
        );
        if (result.warning) setLinkWarning(result.warning);
        else setLinkWarning(null);
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Verknüpfung fehlgeschlagen';
        setError(message);
        throw new Error(message);
      })
      .finally(() => setLinkingEmailId(null));
  };

  const getThreadCount = React.useCallback(
    (mail: InboxEmail) => mail.thread_count || 1,
    [],
  );
  const filteredEmails = emails;

  React.useEffect(() => {
    if (emailIdParam != null && emailIdParam !== '' && !urlMailId) {
      openInboxMail(null, { replace: true });
    }
  }, [emailIdParam, urlMailId, openInboxMail]);

  React.useEffect(() => {
    if (!urlMailId) {
      setThreadMails([]);
      setThreadLoading(false);
      setDeepLinkMail(null);
      return;
    }
    let cancelled = false;
    setThreadLoading(true);
    fetch(`/api/inbox/emails/${encodeURIComponent(urlMailId)}/thread`)
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (!r.ok) throw new Error();
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        const list = parseEmailsWithAttachments(Array.isArray(data) ? data : []);
        setThreadMails((prev) => {
          const fetchedIds = new Set(list.map((mail) => String(mail.id)));
          const extras = prev.filter((mail) => {
            if (fetchedIds.has(String(mail.id))) return false;
            return liveRowBelongsToOpenThread(mail, urlMailId, list, list[0] ?? null);
          });
          return extras.length > 0 ? [...list, ...extras] : list;
        });
        const found = list.find((m) => String(m.id) === urlMailId) ?? list[0] ?? null;
        setDeepLinkMail(found);
      })
      .catch(() => {
        if (cancelled) return;
        setThreadMails([]);
        fetch(`/api/inbox/emails/${encodeURIComponent(urlMailId)}`)
          .then(async (r) => {
            if (!r.ok) throw new Error();
            return r.json();
          })
          .then((row) => {
            if (cancelled) return;
            const [parsed] = parseEmailsWithAttachments([row]);
            setDeepLinkMail(parsed ?? null);
            setThreadMails(parsed ? [parsed] : []);
          })
          .catch(() => {
            if (!cancelled) setDeepLinkMail(null);
          });
      })
      .finally(() => {
        if (!cancelled) setThreadLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [urlMailId, parseEmailsWithAttachments]);

  const selectedMail = React.useMemo(() => {
    if (!urlMailId) return null;
    const fromThread = threadMails.find((m) => String(m.id) === urlMailId);
    if (fromThread) return fromThread;
    if (deepLinkMail && String(deepLinkMail.id) === urlMailId) return deepLinkMail;
    return resolveInboxMailForView(urlMailId, emails, emails);
  }, [urlMailId, threadMails, deepLinkMail, emails]);
  selectedMailRef.current = selectedMail;
  const detailIndex = selectedMail
    ? filteredEmails.findIndex((mail) => String(mail.id) === String(selectedMail.id))
    : -1;
  const openAdjacentMail = React.useCallback((offset: number) => {
    if (detailIndex < 0) return;
    const next = filteredEmails[detailIndex + offset];
    if (next) openInboxMail(next.id);
  }, [detailIndex, filteredEmails, openInboxMail]);

  const estimateReplyOutgoingBodyBytes = React.useCallback(
    (text: string, html: string) => {
      const quoteHtml =
        replyQuoteEnabled && selectedMail
          ? buildInboxReplyQuoteBlocks(selectedMail).html
          : null;
      return estimateNylasOutgoingBodyBytes({
        text,
        html,
        signatureHtml: selectedAccount?.signature_html,
        quoteHtml,
      });
    },
    [replyQuoteEnabled, selectedMail, selectedAccount?.signature_html],
  );

  React.useEffect(() => {
    setPreviewAttachment(null);
  }, [urlMailId, selectedAccountId]);

  const inboxDocumentTitle = React.useMemo(() => {
    if (!selectedMail) return null;
    const subject = String(selectedMail.subject || '').trim();
    return subject || 'Kein Betreff';
  }, [selectedMail]);
  useDocumentTitle(inboxDocumentTitle);

  const selectedThreadKey = React.useMemo(() => {
    if (!urlMailId) return null;
    const anchor = emails.find((m) => String(m.id) === urlMailId);
    return anchor ? inboxThreadKey(anchor) : null;
  }, [urlMailId, emails]);

  React.useEffect(() => {
    if (loadingAccounts || loadingEmails || emailsSilentRefreshing) return;
    // Only act once the loaded list belongs to the currently selected account.
    // Otherwise a deep link could be cleared before its mail has been fetched.
    if (selectedAccountId != null && String(loadedEmailsAccountId) !== String(selectedAccountId)) return;

    if (!urlMailId) return;
    const inCache = emails.some((m) => String(m.id) === urlMailId);
    if (inCache) return;
    if (threadLoading) return;
    if (deepLinkMail && String(deepLinkMail.id) === urlMailId) return;
    if (threadMails.some((m) => String(m.id) === urlMailId)) return;
    openInboxMail(null, { replace: true });
  }, [
    loadingAccounts,
    loadingEmails,
    emailsSilentRefreshing,
    urlMailId,
    emails,
    selectedAccountId,
    loadedEmailsAccountId,
    filteredEmails,
    openInboxMail,
    isMobile,
    threadLoading,
    deepLinkMail,
    threadMails,
  ]);
  const selectedThread: ChatMessage[] = React.useMemo(() => {
    if (!selectedMail) return [];
    const accountEmail = selectedAccount?.email?.toLowerCase() || '';
    const rawThreadMails = filterEmailsInThread(
      selectedMail,
      threadMails.length > 0 ? threadMails : emails,
    ).sort((a, b) => new Date(b.date_at || '').getTime() - new Date(a.date_at || '').getTime());
    const dedupedThreadMails = dedupeThreadMails(rawThreadMails, accountEmail);

    const threadMessages: ChatMessage[] = dedupedThreadMails.map((m) => {
      const rawText = m.body_text || m.snippet || m.subject || '(Kein Inhalt)';
      const plainParts = splitQuotedPlainText(normalizeMailBody(rawText));
      let incomingText = plainParts.main;
      let quotedText = plainParts.quote;
      let displayHtml = m.body_html?.trim() ? m.body_html : undefined;
      let quotedHtml: string | null | undefined;
      if (displayHtml) {
        const htmlParts = splitQuotedHtml(displayHtml);
        displayHtml = htmlParts.main && !isHtmlEditorEmpty(htmlParts.main) ? htmlParts.main : undefined;
        quotedHtml = htmlParts.quote;
        // Prefer HTML quote when found; otherwise keep plaintext quote for the expander.
        if (quotedHtml) {
          quotedText = null;
        }
      }
      if (displayHtml && isHtmlEditorEmpty(displayHtml)) {
        displayHtml = undefined;
      }
      if (!displayHtml && !incomingText.trim()) {
        incomingText = normalizeMailBody(m.snippet || m.subject || '(Kein Inhalt)');
      }
      if (displayHtml) {
        displayHtml = rewriteInlineMailHtml(displayHtml, m);
      }
      if (quotedHtml) {
        quotedHtml = rewriteInlineMailHtml(quotedHtml, m);
      }
      const fromMe = isOutgoingFromAccount(m.from_addr, accountEmail);
      const attachments = Array.isArray(m.attachments) ? m.attachments : [];
      return {
        id: `mail-${m.id}`,
        from: fromMe ? 'me' : 'them',
        senderAddr: fromMe ? accountEmail : String(m.from_addr || ''),
        text: incomingText,
        html: displayHtml,
        quotedText: quotedText || null,
        quotedHtml: quotedHtml || null,
        created_at: m.date_at || new Date().toISOString(),
        attachments: attachments.length > 0 ? attachments : undefined,
        external_message_id: m.external_message_id ?? null,
        account_id: m.account_id,
      };
    });
    return threadMessages.sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [selectedMail, emails, threadMails, selectedAccount, rewriteInlineMailHtml]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showComposeModal) setShowComposeModal(false);
        if (showReplyModal) setShowReplyModal(false);
        if (showAttachmentsModal) setShowAttachmentsModal(false);
        if (showLinkModal) setShowLinkModal(false);
      }
    };
    if (showComposeModal || showReplyModal || showAttachmentsModal || showLinkModal) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showComposeModal, showReplyModal, showAttachmentsModal, showLinkModal]);

  React.useEffect(() => {
    const isTypingTarget = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.isContentEditable) return true;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      return Boolean(el.closest('[contenteditable="true"]'));
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (!selectedMail) return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const deleteKey = e.key === 'Delete' || e.key === 'Backspace';
      if (!deleteKey && e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      if (isTypingTarget(e.target)) return;
      if (
        showComposeModal
        || showReplyModal
        || showAttachmentsModal
        || showLinkModal
        || showCreateLeadModal
        || showCreateActivityModal
        || previewAttachment
      ) {
        return;
      }

      e.preventDefault();
      if (deleteKey) {
        // Entf moves the open mail to the trash and opens the next one, like in Outlook.
        const next = detailIndex >= 0 ? filteredEmails[detailIndex + 1] ?? filteredEmails[detailIndex - 1] : null;
        handleDeleteRef.current(selectedMail.id, { openNext: next?.id ?? null });
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') openAdjacentMail(1);
      else openAdjacentMail(-1);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    detailIndex,
    filteredEmails,
    openAdjacentMail,
    previewAttachment,
    selectedMail,
    showAttachmentsModal,
    showComposeModal,
    showCreateActivityModal,
    showCreateLeadModal,
    showLinkModal,
    showReplyModal,
  ]);

  const handleSendReply = (modeOverride?: ReplyMode) => {
    if (!selectedMail || replySending) return;
    const html = (replyEditorRef.current?.innerHTML || replyDraft).trim();
    const text = normalizeMailBody(replyEditorRef.current?.innerText || '');
    if (!text) return;
    const attachError = validateNylasAttachments(
      replyAttachments.map((f) => ({ name: f.name, size: f.size })),
      {
        bodyTextBytes: estimateReplyOutgoingBodyBytes(text, html),
      },
    );
    if (attachError) {
      setAttachmentError(attachError);
      return;
    }
    setAttachmentError(null);
    const hasAttachments = replyAttachments.length > 0;
    const effectiveMode = modeOverride ?? replyMode;
    const replyAll = effectiveMode === 'replyAll' && canReplyAll;
    const replyAllCc = canReplyAll ? buildReplyAllCc(selectedMail, accountEmail) : '';
    const ccValue = resolveInboxReplyCc(replyAll, replyCc, replyAllCc);
    const body: BodyInit = hasAttachments
      ? (() => {
          const fd = new FormData();
          fd.append('text', text);
          if (html) fd.append('html', html);
          if (ccValue) fd.append('cc', ccValue);
          if (replyAll) fd.append('replyAll', 'true');
          replyAttachments.forEach((file) => fd.append('attachments', file));
          return fd;
        })()
      : JSON.stringify({ text, ...(html ? { html } : {}), ...(ccValue ? { cc: ccValue } : {}), ...(replyAll ? { replyAll: true } : {}) });
    if (replyAll) {
      setReplyMode('replyAll');
      setReplyCc(ccValue);
      setShowReplyCc(Boolean(ccValue));
    }
    setReplySending(true);
    fetch(`/api/inbox/emails/${selectedMail.id}/reply`, {
      method: 'POST',
      headers: hasAttachments ? undefined : { 'Content-Type': 'application/json' },
      body,
    })
      .then((r) =>
        r
          .text()
          .then((t) => {
            let d: any = {};
            try {
              d = t ? JSON.parse(t) : {};
            } catch {
              d = { raw: t };
            }
            return { ok: r.ok, d };
          })
      )
      .then(async ({ ok, d }) => {
        if (!ok || !d?.success) throw new Error(d?.error || 'Antwort konnte nicht gesendet werden');
        if (d.email) {
          const [parsed] = parseEmailsWithAttachments([d.email]);
          if (parsed) {
            setEmails((prev) => mergeInboxThreadListRow(prev, parsed));
            setThreadMails((prev) => mergeInboxEmailRow(prev, parsed));
          }
        }
        setReplyDraft('');
        if (replyEditorRef.current) {
          replyEditorRef.current.innerHTML = '';
        }
        setReplyAttachments([]);
        setReplyCc('');
        setReplyMode('reply');
        setShowReplyCc(false);
        try {
          await reloadEmails({ silent: true });
        } catch {
          setError('E-Mail wurde gesendet, aber die Ansicht konnte nicht aktualisiert werden.');
        }
      })
      .catch((err) => {
        setError(err.message || 'Antwort konnte nicht gesendet werden');
      })
      .finally(() => {
        setReplySending(false);
      });
  };

  const openNewCompose = React.useCallback(() => {
    setError(null);
    setComposeInitial(null);
    setComposeResetKey((k) => k + 1);
    setShowComposeModal(true);
  }, []);

  const openForwardCompose = React.useCallback(() => {
    if (!selectedMail) return;
    const originalSubject = String(selectedMail.subject || '(Kein Betreff)');
    const subject = originalSubject.toLowerCase().startsWith('fwd:') ? originalSubject : `Fwd: ${originalSubject}`;
    const from = String(selectedMail.from_addr || 'Unbekannt');
    const date = selectedMail.date_at
      ? new Date(selectedMail.date_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
      : '—';
    const bodyText = normalizeMailBody(String(selectedMail.body_text || selectedMail.snippet || ''));
    const body = `\n\n--- Weitergeleitete Nachricht ---\nVon: ${from}\nDatum: ${date}\nBetreff: ${originalSubject}\n\n${bodyText}`;
    // Wie bei Gmail: Anhänge der Originalmail werden beim Weiterleiten übernommen.
    const forwardAttachments: InboxAttachmentPreviewTarget[] = [];
    if (selectedMail.account_id && selectedMail.external_message_id) {
      for (const att of selectedMail.attachments ?? []) {
        if (!att.id) continue;
        const filename = att.filename || 'attachment';
        forwardAttachments.push({
          downloadUrl: buildInboxAttachmentDownloadUrl({
            accountId: selectedMail.account_id,
            messageId: selectedMail.external_message_id,
            attachmentId: att.id,
            filename,
          }),
          filename,
          contentType: att.content_type ?? null,
        });
      }
    }
    setError(null);
    setComposeInitial({
      subject,
      body,
      forwardSourceEmailId: selectedMail.id,
      forwardAttachments,
    });
    setComposeResetKey((k) => k + 1);
    setShowComposeModal(true);
  }, [selectedMail]);

  const handleGenerateAiReplyDraft = React.useCallback(
    async (promptOverride?: string) => {
      if (!selectedMail) return;
      const prompt = (promptOverride ?? aiReplyPrompt).trim();
      if (promptOverride !== undefined) setAiReplyPrompt(promptOverride);
      setAiReplyLoading(true);
      setAiReplyError(null);
      try {
        const resp = await fetch('/api/inbox/reply-draft', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailId: selectedMail.id, prompt }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data?.draft) {
          throw new Error(data?.error || 'KI-Entwurf konnte nicht erstellt werden.');
        }
        const draft = String(data.draft || '').trim();
        if (!draft) throw new Error('KI-Entwurf war leer.');
        const draftHtml = markdownToEmailHtml(draft);
        if (replyEditorRef.current) {
          replyEditorRef.current.innerHTML = draftHtml;
          replyEditorRef.current.focus();
        }
        setReplyDraft(draftHtml);
      } catch (err) {
        setAiReplyError(err instanceof Error ? err.message : 'KI-Entwurf konnte nicht erstellt werden.');
      } finally {
        setAiReplyLoading(false);
      }
    },
    [selectedMail, aiReplyPrompt]
  );

  const selectedAnf = React.useMemo(() => {
    if (!selectedMail || selectedMail.lead_id == null) return null;
    const l = leadsForLink.find((x) => String(x.id) === String(selectedMail.lead_id));
    return l ? l.display_id : `#${selectedMail.lead_id}`;
  }, [selectedMail, leadsForLink]);
  const linkedLead = React.useMemo(() => {
    if (!selectedMail?.lead_id) return null;
    return leadsForLink.find((x) => String(x.id) === String(selectedMail.lead_id)) ?? null;
  }, [selectedMail?.lead_id, leadsForLink]);

  const activityPrefill = React.useMemo(() => {
    if (!selectedMail) return null;
    const subject = String(selectedMail.subject || '').trim() || 'E-Mail';
    const from = String(selectedMail.from_addr || 'Unbekannt');
    const date = selectedMail.date_at
      ? new Date(selectedMail.date_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
      : null;
    const snippet = String(selectedMail.snippet || selectedMail.body_text || '').trim();
    const noteLines = [
      date ? `Bezug: E-Mail vom ${date}` : 'Bezug: Aktuelle E-Mail im Posteingang',
      `Von: ${from}`,
      `Betreff: ${subject}`,
      snippet ? `\n${snippet}` : '',
    ].filter(Boolean);
    return {
      defaultLeadId: linkedLead ? String(linkedLead.id) : undefined,
      defaultTitle: `Rückruf: ${subject}`.slice(0, 200),
      defaultNotes: noteLines.join('\n').slice(0, 2000),
    };
  }, [selectedMail, linkedLead]);

  React.useEffect(() => {
    if (!selectedMail) {
      setShowCreateActivityModal(false);
    }
  }, [selectedMail]);

  React.useEffect(() => {
    setAiReplyPrompt('');
    setAiReplyError(null);
    setAiReplyLoading(false);
  }, [selectedMail?.id]);

  const canReplyAll = React.useMemo(
    () => (selectedMail ? shouldOfferReplyAll(selectedMail, accountEmail) : false),
    [selectedMail, accountEmail],
  );

  const handleReplyMode = React.useCallback(
    (mode: ReplyMode) => {
      setReplyMode(mode);
      if (!selectedMail) {
        setReplyCc('');
        setShowReplyCc(false);
        return;
      }
      if (mode === 'replyAll' && canReplyAll) {
        const nextCc = buildReplyAllCc(selectedMail, accountEmail);
        setReplyCc(nextCc);
        setShowReplyCc(Boolean(nextCc));
        return;
      }
      setReplyCc('');
      setShowReplyCc(false);
    },
    [selectedMail, canReplyAll, accountEmail],
  );

  React.useEffect(() => {
    setReplyMode('reply');
    setReplyCc('');
    setShowReplyCc(false);
  }, [selectedMail?.id]);

  const replySuggestions = React.useMemo(() => {
    if (!selectedMail) return [] as string[];
    const bucket = getTriageBucket(selectedMail);
    if (bucket === 'antwort') return ['Termin anbieten', 'Eingang bestätigen', 'Rückfrage stellen'];
    if (bucket === 'warten') return ['Freundlich nachfassen', 'Status erfragen'];
    return ['Freundlich antworten', 'Eingang bestätigen'];
  }, [selectedMail]);

  const emailsInitialLoading = loadingEmails && emails.length === 0;
  // Match the list's direction, so Posteingang shows the same number as the rail.
  const listDirection = searchingAll ? 'alle' : directionTab;
  const directionTotal =
    listDirection === 'in' ? directionCounts.in : listDirection === 'out' ? directionCounts.out : directionCounts.alle;
  const listTotalCount = typeof folderHeaderCount === 'number' ? folderHeaderCount : directionTotal;
  const emailsFetching = syncing || emailsInitialLoading;
  const syncIconSpinning = syncing || backgroundSyncing;
  const syncButtonLabel = syncing
    ? syncProgress
      ? `Wird abgerufen… (${syncProgress.current}/${syncProgress.total})`
      : 'Wird abgerufen…'
    : emailsInitialLoading
      ? 'Wird geladen…'
      : 'E-Mails abrufen';
  const syncButtonTitle =
    syncing && syncProgress
      ? `E-Mails abrufen (${syncProgress.current} von ${syncProgress.total})`
      : syncing
        ? 'E-Mails werden abgerufen…'
        : emailsInitialLoading
          ? 'E-Mails werden geladen…'
          : 'E-Mails abrufen';

  return (
    <div className="h-screen flex bg-app-bg font-sans overflow-hidden">
      <AppSidebar active="inbox" />

      <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden pb-16 md:pb-0 bg-app-surface">
        <AppTopBar title="Posteingang" />

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {(error || attachmentError) && (
            <div className="shrink-0 mx-3 mt-2 mb-2 md:mx-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex justify-between items-center">
              <span>{attachmentError || error}</span>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setAttachmentError(null);
                }}
                className="text-red-500 hover:underline"
              >
                Schließen
              </button>
            </div>
          )}
          {linkWarning && (
            <div className="shrink-0 mx-3 mt-2 mb-2 md:mx-4 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm border border-amber-200 flex justify-between items-center">
              <span>{linkWarning}</span>
              <button
                type="button"
                onClick={() => setLinkWarning(null)}
                className="text-amber-700 hover:underline"
              >
                Schließen
              </button>
            </div>
          )}
          {showSyncSinceModal && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              onClick={() => { setShowSyncSinceModal(false); setPendingSyncAccountId(null); }}
            >
              <div
                className="bg-app-surface rounded-xl shadow-lg border border-app-border p-6 max-w-sm w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="font-semibold text-app-text mb-2">E-Mails abrufen</h3>
                <p className="text-sm text-app-text-muted mb-4">
                  Ab wann sollen E-Mails synchronisiert werden? Es werden nur E-Mails ab diesem Datum abgerufen.
                </p>
                <DatePicker
                  value={syncSinceDate}
                  onChange={setSyncSinceDate}
                  className="w-full mb-4"
                  placeholder="TT.MM.JJJJ"
                />
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => { setShowSyncSinceModal(false); setPendingSyncAccountId(null); }}
                    className="px-3 py-2 rounded-lg border border-app-border text-sm text-app-text-muted hover:bg-app-hover"
                  >
                    Abbrechen
          </button>
                  <button
                    type="button"
                    onClick={handleSyncSinceConfirm}
                    className="px-3 py-2 rounded-lg bg-pipedrive-blue text-white text-sm hover:bg-blue-700"
                  >
                    Abrufen
                  </button>
            </div>
            </div>
            </div>
          )}

          {loadingAccounts && accounts.length === 0 ? (
            <PageLoadingState />
          ) : accounts.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-md w-full border border-app-border rounded-lg p-12 text-center text-app-text-subtle bg-app-surface">
              <Inbox className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Noch kein E-Mail-Konto verbunden.</p>
              <p className="text-sm mt-1">Bitte verbinden Sie ein Konto in den Einstellungen.</p>
              <Link
                to={`${instancePrefix}/settings/email-dispatch`}
                className="inline-flex mt-4 px-3 py-2 rounded-lg bg-pipedrive-blue text-white text-sm hover:bg-blue-700"
              >
                Zu den Einstellungen
          </Link>
            </div>
            </div>
          ) : (
            <section className="flex-1 flex flex-col min-h-0 overflow-hidden min-w-0">
              <div className="flex-1 min-h-0 min-w-0 flex overflow-hidden bg-app-bg">
                <InboxFolderNav
                  accountId={selectedAccountId}
                  selectedFolderId={folderExternalId}
                  starredOnly={starredOnly}
                  urgentOnly={urgentOnly}
                  activeStatus={statusFilter.length === 1 ? statusFilter[0] : null}
                  activeAssigneeId={assignedUserId}
                  members={inboxMembers}
                  onSelectFolder={(id) => {
                    setFolderExternalId(id);
                    setStarredOnly(false);
                    setUrgentOnly(false);
                    setStatusFilter([]);
                    setAssignedUserId(null);
                    setMobileFoldersOpen(false);
                    openInboxMail(null);
                  }}
                  onSelectStarred={() => {
                    setStarredOnly(true);
                    setUrgentOnly(false);
                    setStatusFilter([]);
                    setAssignedUserId(null);
                    setFolderExternalId(null);
                    setMobileFoldersOpen(false);
                    openInboxMail(null);
                  }}
                  onSelectUrgent={() => {
                    setUrgentOnly((current) => !current);
                    setStarredOnly(false);
                    setStatusFilter([]);
                    setAssignedUserId(null);
                    setFolderExternalId(null);
                    setMobileFoldersOpen(false);
                    openInboxMail(null);
                  }}
                  onSelectStatus={(status) => {
                    setStatusFilter(status ? [status] : []);
                    setStarredOnly(false);
                    setUrgentOnly(false);
                    setAssignedUserId(null);
                    setFolderExternalId(null);
                    setMobileFoldersOpen(false);
                    openInboxMail(null);
                  }}
                  onSelectAssignee={(userId) => {
                    setAssignedUserId(userId);
                    setStarredOnly(false);
                    setUrgentOnly(false);
                    setFolderExternalId(null);
                    setMobileFoldersOpen(false);
                    openInboxMail(null);
                  }}
                  onNewMail={() => {
                    openInboxMail(null);
                    openNewCompose();
                  }}
                  onAssignEmailsToCategory={handleAssignEmailsToCategory}
                  onAssignEmailsUrgent={handleAssignEmailsUrgent}
                  onAssignEmailsToStatus={handleAssignEmailsToStatus}
                  onAssignEmailsToUser={handleAssignEmailsToUser}
                  onFoldersChange={setMailboxFolders}
                  mailboxCounts={mailboxCounts}
                  countsRevision={statusCountsRevision}
                  accountOptions={accounts.map((a) => ({ value: String(a.id), label: a.email }))}
                  selectedAccountId={selectedAccountId != null ? String(selectedAccountId) : null}
                  onAccountChange={(value) => {
                    setSelectedAccountId(value || null);
                    openInboxMail(null);
                  }}
                  mobileOpen={mobileFoldersOpen}
                  onCloseMobile={() => setMobileFoldersOpen(false)}
                />
                {!urlMailId ? (
                <InboxThreadList
                  threads={filteredEmails}
                  accountEmail={accountEmail}
                  selectedAccountId={selectedAccountId != null ? String(selectedAccountId) : null}
                  accountOptions={accounts.map((a) => ({ value: String(a.id), label: a.email }))}
                  syncButtonLabel={syncButtonLabel}
                  syncButtonTitle={syncButtonTitle}
                  syncIconSpinning={syncIconSpinning}
                  emailsFetching={emailsFetching}
                  onAccountChange={(v) => setSelectedAccountId(v || null)}
                  onSync={() => {
                    if (selectedAccount) handleSync(selectedAccount.id);
                  }}
                  selectedMailId={urlMailId}
                  selectedThreadKey={selectedThreadKey}
                  onSelect={(id) => {
                    openInboxMail(id);
                    const mail = emails.find((row) => String(row.id) === String(id));
                    if (mail?.is_unread) {
                      setEmails((prev) => prev.map((row) => (String(row.id) === String(id) ? { ...row, is_unread: false } : row)));
                      void markInboxEmailRead(id);
                    }
                  }}
                  searchTerm={searchTerm}
                  onSearch={setSearchTerm}
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                  sortMode={sortMode}
                  onSortMode={setSortMode}
                  getThreadCount={getThreadCount}
                  leadsForLink={leadsForLink}
                  onOpenProject={(leadId) => navigate(`${instancePrefix}/projects/${leadId}`)}
                  initialLoading={emailsInitialLoading}
                  hasAnyEmails={
                    emails.length > 0 ||
                    directionCounts.alle > 0 ||
                    Boolean(searchTerm.trim()) ||
                    statusFilter.length > 0 ||
                    starredOnly ||
                    urgentOnly ||
                    Boolean(assignedUserId) ||
                    Boolean(listFolderId) ||
                    directionTab !== 'alle'
                  }
                  onNewMail={openNewCompose}
                  isMobile={isMobile}
                  hasMore={hasMore}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  loadingMore={loadingMore}
                  onNextPage={goNext}
                  onPrevPage={goPrev}
                  onToggleStar={handleToggleStar}
                  onOpenAttachment={openInboxAttachment}
                  onArchive={(mail) => handleArchive(mail.id)}
                  onDelete={(mail) => handleDelete(mail.id)}
                  onMarkRead={(mail) => {
                    if (!mail.is_unread) return;
                    setEmails((prev) => prev.map((row) => (String(row.id) === String(mail.id) ? { ...row, is_unread: false } : row)));
                    void markInboxEmailRead(mail.id);
                  }}
                  onMarkDone={(mail) => handleChangeStatus(mail.id, 'done')}
                  onBulkAction={handleBulkAction}
                  onOpenFolders={() => setMobileFoldersOpen(true)}
                  totalCount={listTotalCount}
                  leavingIds={leavingIds}
                  keyboardEnabled={
                    !showComposeModal
                    && !showReplyModal
                    && !showAttachmentsModal
                    && !showLinkModal
                    && !showCreateLeadModal
                    && !showCreateActivityModal
                    && !previewAttachment
                  }
                />
                ) : null}

                <div className={`min-h-0 min-w-0 bg-app-surface md:bg-app-bg md:p-3 ${urlMailId ? 'flex flex-1 flex-col' : 'hidden'}`}>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-app-surface md:rounded-xl md:border md:border-app-border">
                  {selectedMail ? (
                    <div className="flex h-14 shrink-0 items-center gap-0.5 px-1 md:h-12 md:border-b md:border-app-border md:px-3">
                      <button
                        type="button"
                        onClick={() => { openInboxMail(null, { replace: true }); setShowMobileDetails(false); }}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover md:mr-2 md:h-[34px] md:w-[34px]"
                        title="Zurück zum Posteingang"
                        aria-label="Zurück zum Posteingang"
                      >
                        <ArrowLeft className="h-5 w-5 md:h-4 md:w-4" strokeWidth={2} />
                      </button>
                      <span className="flex-1 md:hidden" />
                      <button
                        type="button"
                        onClick={() => handleArchive(selectedMail.id)}
                        disabled={archivingId != null && String(archivingId) === String(selectedMail.id)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-40 md:h-[34px] md:w-[34px]"
                        title="Archivieren"
                        aria-label="Archivieren"
                      >
                        <Archive className="h-5 w-5 md:h-4 md:w-4" strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(selectedMail.id)}
                        disabled={deletingId != null && String(deletingId) === String(selectedMail.id)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-40 md:h-[34px] md:w-[34px]"
                        title="Löschen"
                        aria-label="Löschen"
                      >
                        <Trash2 className="h-5 w-5 md:h-4 md:w-4" strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChangeStatus(selectedMail.id, 'done')}
                        title="Als erledigt markieren"
                        aria-label="Als erledigt markieren"
                        className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-full text-xs font-medium text-app-text-muted hover:bg-app-hover md:ml-4 md:h-7 md:w-auto md:rounded-md md:border md:border-app-border md:bg-app-surface md:px-2.5"
                      >
                        <Check className="h-5 w-5 text-green-600 md:h-3.5 md:w-3.5" strokeWidth={2.2} />
                        <span className="hidden md:inline">Als erledigt markieren</span>
                      </button>
                      <div ref={mobileMailMenuRef} className="relative md:hidden">
                        <button
                          type="button"
                          onClick={() => setShowMobileMailMenu((open) => !open)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover"
                          aria-label="Weitere Aktionen"
                          aria-expanded={showMobileMailMenu}
                        >
                          <MoreVertical className="h-5 w-5" />
                        </button>
                        {showMobileMailMenu ? (
                          <div className="absolute right-1 top-full z-50 mt-1 w-56 overflow-hidden rounded-xl border border-app-border bg-app-surface py-1 shadow-lg">
                            <button
                              type="button"
                              onClick={() => { setShowMobileDetails(true); setShowMobileMailMenu(false); }}
                              className="flex h-12 w-full items-center gap-3 px-4 text-[15px] text-app-text hover:bg-app-hover disabled:opacity-40"
                            >
                              <Info className="h-5 w-5 text-app-text-muted" />
                              Details & Projekt
                            </button>
                            <button
                              type="button"
                              disabled={detailIndex <= 0}
                              onClick={() => { openAdjacentMail(-1); setShowMobileMailMenu(false); }}
                              className="flex h-12 w-full items-center gap-3 px-4 text-[15px] text-app-text hover:bg-app-hover disabled:opacity-40"
                            >
                              <ChevronLeft className="h-5 w-5 text-app-text-muted" />
                              Neuere E-Mail
                            </button>
                            <button
                              type="button"
                              disabled={detailIndex < 0 || detailIndex >= filteredEmails.length - 1}
                              onClick={() => { openAdjacentMail(1); setShowMobileMailMenu(false); }}
                              className="flex h-12 w-full items-center gap-3 px-4 text-[15px] text-app-text hover:bg-app-hover disabled:opacity-40"
                            >
                              <ChevronRight className="h-5 w-5 text-app-text-muted" />
                              Ältere E-Mail
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <span className="hidden flex-1 md:block" />
                      <span className="mr-1.5 hidden text-xs tabular-nums text-app-text-subtle md:inline">
                        {detailIndex >= 0 ? `${detailIndex + 1} von ${Math.max(listTotalCount, filteredEmails.length)}` : ''}
                      </span>
                      <button
                        type="button"
                        disabled={detailIndex <= 0}
                        onClick={() => openAdjacentMail(-1)}
                        className="hidden md:inline-flex h-[30px] w-[30px] items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-40"
                        aria-label="Vorherige E-Mail"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={detailIndex < 0 || detailIndex >= filteredEmails.length - 1}
                        onClick={() => openAdjacentMail(1)}
                        className="hidden md:inline-flex h-[30px] w-[30px] items-center justify-center rounded-full text-app-text-muted hover:bg-app-hover disabled:opacity-40"
                        aria-label="Nächste E-Mail"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                  <div className="flex min-h-0 flex-1">
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                  {selectedMail ? (
                    <InboxConversation
                      selectedMail={selectedMail}
                      messages={selectedThread}
                      messagesLoading={threadLoading}
                      accountEmail={accountEmail}
                      onArchive={() => { handleArchive(selectedMail.id); setShowMobileMailMenu(false); }}
                      onDelete={() => { handleDelete(selectedMail.id); setShowMobileMailMenu(false); }}
                      archiving={archivingId != null && String(archivingId) === String(selectedMail.id)}
                      deleting={deletingId != null && String(deletingId) === String(selectedMail.id)}
                      threadScrollRef={threadScrollRef}
                      sanitizeMailHtml={sanitizeMailHtml}
                      renderTextWithLinks={renderTextWithLinks}
                      onOpenAttachment={openInboxAttachment}
                      onBack={() => { openInboxMail(null, { replace: true }); setShowMobileDetails(false); }}
                      onToggleStar={() => handleToggleStar(selectedMail)}
                      onForward={openForwardCompose}
                      onAiReply={() => { void handleGenerateAiReplyDraft(); }}
                      compose={
                        <InboxComposeArea
                          editorRef={replyEditorRef}
                          fileInputRef={replyFileInputRef}
                          replyDraft={replyDraft}
                          onReplyInput={setReplyDraft}
                          onSend={handleSendReply}
                          canReplyAll={canReplyAll}
                          replyMode={replyMode}
                          onReplyMode={handleReplyMode}
                          sending={replySending}
                          contactName={displayNameFromAddr(
                            replyMode === 'replyAll' && canReplyAll
                              ? getInboxReplyRecipient(selectedMail, accountEmail)
                              : getCounterpartyAddr(selectedMail, accountEmail),
                          )}
                          replyCc={replyCc}
                          onReplyCc={setReplyCc}
                          showReplyCc={showReplyCc}
                          onToggleCc={() => setShowReplyCc((p) => !p)}
                          replyAttachments={replyAttachments}
                          onFilesSelected={(picked) => {
                            const html = (replyEditorRef.current?.innerHTML || replyDraft).trim();
                            const text = normalizeMailBody(replyEditorRef.current?.innerText || replyDraft);
                            const { files, error } = mergeNylasAttachmentFiles(replyAttachments, picked, {
                              bodyTextBytes: estimateReplyOutgoingBodyBytes(text, html),
                            });
                            setAttachmentError(error);
                            if (!error) setReplyAttachments(files);
                          }}
                          onOpenAttachments={() => setShowAttachmentsModal(true)}
                          onForward={openForwardCompose}
                          onLinkButton={() => {
                            const sel = window.getSelection();
                            savedSelectionRef.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
                            setLinkUrl('');
                            setShowLinkModal(true);
                          }}
                          suggestions={replySuggestions}
                          mailId={selectedMail.id}
                          aiPrompt={aiReplyPrompt}
                          onAiPrompt={setAiReplyPrompt}
                          aiLoading={aiReplyLoading}
                          aiError={aiReplyError}
                          onGenerateAiDraft={(prompt) => void handleGenerateAiReplyDraft(prompt)}
                        />
                      }
                    />
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-app-text-subtle text-sm px-4 text-center">
                      {urlMailId && !loadingAccounts && !loadingEmails
                        ? 'Diese E-Mail wurde nicht gefunden oder ist nicht mehr im Posteingang.'
                        : 'Bitte wählen Sie eine E-Mail aus.'}
                    </div>
                  )}
                  </div>

                <aside className={`${urlMailId ? 'hidden md:flex' : 'hidden'} w-[296px] shrink-0 flex-col min-h-0 border-l border-app-border bg-app-surface`}>
                  {selectedMail ? (
                    <InboxContextPanel
                      selectedMail={selectedMail}
                      accountEmail={accountEmail}
                      anfLabel={selectedAnf}
                      statusValue={(selectedMail.classification as MailStatus) || 'other'}
                      onChangeStatus={(s) => handleChangeStatus(selectedMail.id, s)}
                      onChangeUrgency={(level) => handleChangeUrgency(selectedMail.id, level)}
                      onAssignUser={(userId) => handleAssignUser(selectedMail.id, userId, selectedMail.thread_id ?? null)}
                      members={inboxMembers}
                      updatingStatus={updatingStatusId != null && String(updatingStatusId) === String(selectedMail.id)}
                      leadsForLink={leadsForLink}
                      onLink={(leadId) => handleLinkToLead(selectedMail.id, leadId, selectedMail.thread_id ?? null)}
                      onCreateLead={() => setShowCreateLeadModal(true)}
                      linking={linkingEmailId != null && String(linkingEmailId) === String(selectedMail.id)}
                      linkedLead={linkedLead}
                      onOpenLead={() => {
                        if (selectedMail.lead_id != null) navigate(`${instancePrefix}/projects/${selectedMail.lead_id}`);
                      }}
                      onOpenActivity={handleOpenInboxActivity}
                      onOpenContact={() => {
                        const cid = linkedLead?.contact_id ?? linkedLead?.contact?.id;
                        if (cid != null) navigate(contactDetailPath(cid));
                      }}
                      onCreateActivity={() => setShowCreateActivityModal(true)}
                      activitiesRefreshKey={activitiesRefreshKey}
                      categories={mailboxFolders.filter((folder) => !folder.system_folder).map((folder) => ({
                        id: folder.external_folder_id,
                        label: folder.display_name || folder.name,
                      }))}
                      onToggleCategory={(folderId, assigned) => handleToggleCategory(selectedMail, folderId, assigned)}
                    />
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-app-text-subtle text-sm px-4 text-center">
                      Keine E-Mail ausgewählt.
                    </div>
                  )}
                </aside>
                  </div>
                </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      {/* Mobile FAB: Neue E-Mail – nur sichtbar in Listenansicht ohne offenen Thread */}
      {accounts.length > 0 && selectedAccountId != null && !selectedMail && (
        <button
          type="button"
          onClick={openNewCompose}
          className="fixed bottom-20 right-5 z-30 md:hidden h-14 w-14 rounded-full bg-pipedrive-blue text-white shadow-lg flex items-center justify-center hover:bg-blue-700 transition-colors"
          aria-label="Neue E-Mail"
          title="Neue E-Mail"
        >
          <MailPlus className="w-6 h-6" />
        </button>
      )}

      {/* Mobile Details Slide-Over */}
      {showMobileDetails && selectedMail && (
        <>
          <div
            className="fixed inset-0 z-[44] bg-black/40 md:hidden"
            onClick={() => setShowMobileDetails(false)}
          />
          <div className="fixed inset-y-0 right-0 z-[45] w-full bg-app-surface flex flex-col md:hidden">
            <InboxContextPanel
              selectedMail={selectedMail}
              accountEmail={accountEmail}
              anfLabel={selectedAnf}
              statusValue={(selectedMail.classification as MailStatus) || 'other'}
              onChangeStatus={(s) => handleChangeStatus(selectedMail.id, s)}
              onChangeUrgency={(level) => handleChangeUrgency(selectedMail.id, level)}
              onAssignUser={(userId) => handleAssignUser(selectedMail.id, userId, selectedMail.thread_id ?? null)}
              members={inboxMembers}
              updatingStatus={updatingStatusId != null && String(updatingStatusId) === String(selectedMail.id)}
              leadsForLink={leadsForLink}
              onLink={(leadId) => handleLinkToLead(selectedMail.id, leadId, selectedMail.thread_id ?? null)}
              onCreateLead={() => setShowCreateLeadModal(true)}
              linking={linkingEmailId != null && String(linkingEmailId) === String(selectedMail.id)}
              linkedLead={linkedLead}
              onOpenLead={() => {
                if (selectedMail.lead_id != null) navigate(`${instancePrefix}/projects/${selectedMail.lead_id}`);
              }}
              onOpenActivity={handleOpenInboxActivity}
              onOpenContact={() => {
                const cid = linkedLead?.contact_id ?? linkedLead?.contact?.id;
                if (cid != null) navigate(contactDetailPath(cid));
              }}
              onCreateActivity={() => setShowCreateActivityModal(true)}
              activitiesRefreshKey={activitiesRefreshKey}
              categories={mailboxFolders.filter((folder) => !folder.system_folder).map((folder) => ({
                id: folder.external_folder_id,
                label: folder.display_name || folder.name,
              }))}
              onToggleCategory={(folderId, assigned) => handleToggleCategory(selectedMail, folderId, assigned)}
              onClose={() => setShowMobileDetails(false)}
            />
          </div>
        </>
      )}
      {showCreateLeadModal && selectedMail && can('web.projekte.edit') && (
        <CreateLeadModal
          key={selectedMail.id}
          prefillAnchorEmailId={String(selectedMail.id)}
          onClose={() => setShowCreateLeadModal(false)}
          onCreated={(lead: Lead) => {
            handleLinkToLead(selectedMail.id, lead.id, selectedMail.thread_id ?? null);
            setLeadsForLink((prev) => {
              const next: LeadOption[] = [
                ...prev,
                mapApiLeadToInboxLeadOption(lead),
              ];
              next.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
              return next;
            });
            setShowCreateLeadModal(false);
          }}
        />
      )}
      {showCreateActivityModal && activityPrefill && (
        <CreateActivityModal
          key={selectedMail?.id}
          defaultLeadId={activityPrefill.defaultLeadId}
          defaultTitle={activityPrefill.defaultTitle}
          defaultNotes={activityPrefill.defaultNotes}
          onClose={() => setShowCreateActivityModal(false)}
          onCreated={async (activity) => {
            if (selectedMail && activity?.id) {
              const linkRes = await fetch(`/api/inbox/emails/${encodeURIComponent(selectedMail.id)}/tasks`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ activity_id: activity.id }),
              });
              if (!linkRes.ok) {
                const body = (await linkRes.json().catch(() => ({}))) as { error?: string };
                throw new Error(body.error || 'Aktivität konnte nicht mit der E-Mail verknüpft werden.');
              }
              const activityLeadId = activity.lead_id;
              if (activityLeadId != null) {
                const currentLeadId = selectedMail.lead_id ?? null;
                if (String(currentLeadId ?? '') !== String(activityLeadId)) {
                  await handleLinkToLead(selectedMail.id, activityLeadId, selectedMail.thread_id ?? null);
                }
              }
            }
            setActivitiesRefreshKey((k) => k + 1);
          }}
        />
      )}
      <InboxAttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        onError={setAttachmentError}
      />
      <ComposeEmailModal
        open={showComposeModal && selectedAccountId != null}
        accountId={selectedAccountId!}
        forwardSourceEmailId={composeInitial?.forwardSourceEmailId ?? null}
        forwardAttachments={composeInitial?.forwardAttachments}
        initialTo={composeInitial?.to ?? ''}
        initialCc={composeInitial?.cc ?? ''}
        initialSubject={composeInitial?.subject ?? ''}
        initialBody={composeInitial?.body ?? ''}
        signatureHtml={selectedAccount?.signature_html ?? null}
        resetKey={composeResetKey}
        onClose={() => {
          setShowComposeModal(false);
          setComposeInitial(null);
        }}
        onSent={() => reloadEmails({ silent: true })}
      />
      {showLinkModal && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
          onClick={() => setShowLinkModal(false)}
        >
          <div
            className="bg-app-surface rounded-xl shadow-2xl max-w-md w-[90vw] border border-app-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-app-border">
              <p className="text-sm font-semibold text-app-text">Link einfügen</p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <label className="block text-xs font-medium text-app-text-muted mb-1">
                Link-URL
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="mt-1 w-full border border-app-border-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
                />
              </label>
            </div>
            <div className="px-5 py-3 border-t border-app-border flex items-center justify-end gap-2">
                      <button
                        type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-3 py-2 rounded-lg border border-app-border text-sm text-app-text-muted hover:bg-app-hover"
                      >
                Abbrechen
                      </button>
                      <button
                        type="button"
                disabled={!linkUrl.trim()}
                onClick={() => {
                  const url = linkUrl.trim();
                  if (!url) return;
                  const sel = window.getSelection();
                  if (savedSelectionRef.current && sel) {
                    sel.removeAllRanges();
                    sel.addRange(savedSelectionRef.current);
                  }
                  if (replyEditorRef.current) {
                    replyEditorRef.current.focus();
                  }
                  document.execCommand('createLink', false, url);
                  if (replyEditorRef.current) {
                    setReplyDraft(replyEditorRef.current.innerHTML || '');
                  }
                  setShowLinkModal(false);
                }}
                className="px-3 py-2 rounded-lg bg-pipedrive-blue text-white text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                OK
                      </button>
            </div>
          </div>
        </div>
      )}
      {showAttachmentsModal && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
          onClick={() => setShowAttachmentsModal(false)}
        >
          <div
            className="bg-app-surface rounded-xl shadow-2xl max-w-md w-[90vw] max-h-[70vh] flex flex-col border border-app-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-app-border flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-app-text">Anhänge zur Antwort</p>
              <button
                type="button"
                onClick={() => setShowAttachmentsModal(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text text-xs"
              >
                ✕
              </button>
                            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 text-sm text-app-text-muted">
              {replyAttachments.length === 0 ? (
                <p className="text-xs text-app-text-subtle">Keine Dateien ausgewählt.</p>
              ) : (
                replyAttachments.map((file, idx) => (
                  <div key={`${file.name}-${idx}`} className="flex items-center justify-between gap-2 border border-app-border rounded-lg px-3 py-1.5">
                    <div className="flex flex-col">
                      <span className="font-medium truncate max-w-[200px]">{file.name}</span>
                      <span className="text-xs text-app-text-subtle">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                          </div>
                    <button
                      type="button"
                      onClick={() =>
                        setReplyAttachments((prev) => prev.filter((_, i) => i !== idx))
                      }
                      className="text-xs text-red-600 hover:underline"
                    >
                      Entfernen
                    </button>
                  </div>
                ))
          )}
        </div>
            <div className="px-5 py-3 border-t border-app-border flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => replyFileInputRef.current?.click()}
                className="px-3 py-2 rounded-lg border border-app-border text-sm text-app-text-muted hover:bg-app-hover"
              >
                Weitere Dateien hinzufügen
              </button>
              <button
                type="button"
                onClick={() => setShowAttachmentsModal(false)}
                className="px-3 py-2 rounded-lg bg-pipedrive-blue text-white text-sm hover:bg-blue-700"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      <AppToastViewport toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
